import React, { useState } from 'react';
import { Modal, Typography, Radio, Select, Checkbox, message, Button } from 'antd';
import { IoFilterOutline } from "react-icons/io5";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const { Text } = Typography;

const ModalExportarProductos = ({ 
    openExport, 
    setOpenExport, 
    exportConfig, 
    setExportConfig, 
    productosFiltrados = [], 
    productosDB = [] 
}) => {

    const [isExporting, setIsExporting] = useState(false);

    // Formateador para que los números en Excel y PDF salgan con el signo $ y puntos
    const formatearMoneda = (valor) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(valor || 0);
    };

    // Lógica para armar la tabla de datos según lo que escogió el usuario
    const prepararDatos = () => {
        let dataBase = [];
        if (exportConfig.alcance === 'filtrados') dataBase = productosFiltrados;
        else if (exportConfig.alcance === 'todos') dataBase = productosDB;
        else if (exportConfig.alcance === 'agotados') dataBase = productosDB.filter(p => Number(p.stock) <= 0);

        if (dataBase.length === 0) {
            message.warning("No hay productos que coincidan con la selección para exportar.");
            return [];
        }

        const exportData = [];

        dataBase.forEach(prod => {
            // Si el usuario quiere exportar Códigos, y el producto tiene variantes, 
            // desglosamos las variantes en múltiples filas para un reporte de stock exacto.
            const desglosarVariantes = prod.has_variants && prod.variantes && prod.variantes.length > 0 && exportConfig.columnas.includes('codigo');

            if (desglosarVariantes) {
                prod.variantes.forEach(v => {
                    let row = {};
                    if (exportConfig.columnas.includes('nombre')) row['Nombre del Producto'] = `${prod.nombre} - ${v.atributos?.nombre || 'Variante'}`;
                    if (exportConfig.columnas.includes('codigo')) row['Código / SKU'] = v.codigo_barras || v.codigo_interno || 'Sin Código';
                    if (exportConfig.columnas.includes('categoria')) row['Categoría'] = prod.categoria || 'N/A';
                    if (exportConfig.columnas.includes('precio_compra')) row['Precio Costo'] = formatearMoneda(prod.precio_compra);
                    if (exportConfig.columnas.includes('precio_venta')) row['Precio Venta'] = formatearMoneda(v.precio_catalogo || prod.precio_venta);
                    if (exportConfig.columnas.includes('stock')) row['Stock Actual'] = v.stock || 0;
                    if (exportConfig.columnas.includes('estado')) row['Estado'] = prod.status === 'active' ? 'Activo' : 'Inactivo';
                    exportData.push(row);
                });
            } else {
                let row = {};
                if (exportConfig.columnas.includes('nombre')) row['Nombre del Producto'] = prod.nombre;
                if (exportConfig.columnas.includes('codigo')) row['Código / SKU'] = prod.codigo_barras || prod.codigo_interno || 'Sin Código';
                if (exportConfig.columnas.includes('categoria')) row['Categoría'] = prod.categoria || 'N/A';
                if (exportConfig.columnas.includes('precio_compra')) row['Precio Costo'] = formatearMoneda(prod.precio_compra);
                if (exportConfig.columnas.includes('precio_venta')) row['Precio Venta'] = formatearMoneda(prod.precio_venta);
                if (exportConfig.columnas.includes('stock')) row['Stock Actual'] = prod.stock || 0;
                if (exportConfig.columnas.includes('estado')) row['Estado'] = prod.status === 'active' ? 'Activo' : 'Inactivo';
                exportData.push(row);
            }
        });

        return exportData;
    };

    const exportarExcel = (data) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Productos");
        XLSX.writeFile(workbook, `Productos_Yugonub_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const exportarCSV = (data) => {
        const worksheet = XLSX.utils.json_to_sheet(data);
        const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
        // Agregamos "\uFEFF" (BOM) para que Excel lea los acentos correctamente en el CSV
        const blob = new Blob(["\uFEFF" + csvOutput], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Productos_Yugonub_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
    };

    const exportarPDF = (data) => {
        const doc = new jsPDF('landscape');
        doc.setFontSize(16);
        doc.text("Catálogo de Productos - Yugonub", 14, 15);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(`Generado el: ${new Date().toLocaleDateString()}`, 14, 22);
        
        const columns = Object.keys(data[0]);
        const rows = data.map(obj => Object.values(obj));

        autoTable(doc, {
            head: [columns],
            body: rows,
            startY: 28,
            styles: { fontSize: 9, cellPadding: 3 },
            headStyles: { fillColor: [34, 186, 187], textColor: [255, 255, 255], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [248, 250, 252] },
        });

        doc.save(`Productos_Yugonub_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const handleConfirmarExportacion = () => {
        if (exportConfig.columnas.length === 0) {
            return message.error("Debes seleccionar al menos una columna de datos para exportar.");
        }

        setIsExporting(true);
        const dataToExport = prepararDatos();

        if (dataToExport.length === 0) {
            setIsExporting(false);
            return;
        }

        try {
            if (exportConfig.formato === 'excel') exportarExcel(dataToExport);
            else if (exportConfig.formato === 'csv') exportarCSV(dataToExport);
            else if (exportConfig.formato === 'pdf') exportarPDF(dataToExport);
            
            message.success(`${exportConfig.formato.toUpperCase()} generado correctamente.`);
            setOpenExport(false);
        } catch (error) {
            console.error("Error exportando:", error);
            message.error("Error al generar el archivo. Verifica tu conexión o recarga la página.");
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal
            title={<span style={{ fontWeight: 800, fontSize: '18px', color: '#0f172a' }}>Exportar Catálogo</span>}
            open={openExport}
            onCancel={() => !isExporting && setOpenExport(false)}
            footer={[
                <Button key="back" onClick={() => setOpenExport(false)} disabled={isExporting}>
                    Cancelar
                </Button>,
                <Button 
                    key="submit" 
                    type="primary" 
                    loading={isExporting} 
                    onClick={handleConfirmarExportacion}
                    style={{ backgroundColor: '#10b981', borderColor: '#10b981', fontWeight: 600, borderRadius: '8px' }}
                >
                    Generar {exportConfig.formato.toUpperCase()}
                </Button>
            ]}
            centered
            width={550}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '20px' }}>
                
                {/* PASO 1 */}
                <div>
                    <Text style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>1. Formato del archivo</Text>
                    <div style={{ marginTop: '10px' }}>
                        <Radio.Group 
                            value={exportConfig.formato} 
                            onChange={e => setExportConfig({...exportConfig, formato: e.target.value})}
                            buttonStyle="solid"
                        >
                            <Radio.Button value="excel" style={{ borderRadius: '6px 0 0 6px' }}>Excel (.xlsx)</Radio.Button>
                            <Radio.Button value="csv">CSV (.csv)</Radio.Button>
                            <Radio.Button value="pdf" style={{ borderRadius: '0 6px 6px 0' }}>PDF (.pdf)</Radio.Button>
                        </Radio.Group>
                    </div>
                </div>

                {/* PASO 2 */}
                <div>
                    <Text style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>2. ¿Qué productos exportar?</Text>
                    <Select 
                        value={exportConfig.alcance} 
                        onChange={v => setExportConfig({...exportConfig, alcance: v})}
                        style={{ width: '100%', marginTop: '10px', height: '40px' }}
                    >
                        <Select.Option value="filtrados">Solo la vista actual de la tabla ({productosFiltrados.length} productos)</Select.Option>
                        <Select.Option value="todos">Todo mi catálogo global ({productosDB.length} productos)</Select.Option>
                        <Select.Option value="agotados">Solo productos Agotados (Stock 0)</Select.Option>
                    </Select>
                </div>

                {/* PASO 3 */}
                <div>
                    <Text style={{ fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>3. Datos a incluir (Columnas)</Text>
                    <div style={{ marginTop: '10px', background: '#f8fafc', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <Checkbox.Group 
                            value={exportConfig.columnas} 
                            onChange={v => setExportConfig({...exportConfig, columnas: v})}
                            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}
                        >
                            <Checkbox value="nombre">Nombre / Variante</Checkbox>
                            <Checkbox value="codigo">Código de Barras</Checkbox>
                            <Checkbox value="categoria">Categoría</Checkbox>
                            <Checkbox value="precio_compra">Precio de Costo</Checkbox>
                            <Checkbox value="precio_venta">Precio de Venta</Checkbox>
                            <Checkbox value="stock">Stock Actual</Checkbox>
                            <Checkbox value="estado">Estado (Activo)</Checkbox>
                        </Checkbox.Group>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                        <IoFilterOutline style={{ verticalAlign: 'middle', marginRight: '4px' }}/>
                        Las variantes de tus productos se desglosarán automáticamente si decides exportar el Código de Barras.
                    </Text>
                </div>
            </div>
        </Modal>
    );
};

export default ModalExportarProductos;